// Admin Translation Manager — request / response shapes.

import { z } from 'zod';

import { localeSchema } from './auth';

export const stringStatus = z.enum(['draft', 'published']);
export type StringStatus = z.infer<typeof stringStatus>;

export const translationStringDto = z.object({
  id: z.uuid(),
  key: z.string(),
  locale: localeSchema,
  value: z.string(),
  status: stringStatus,
  updatedBy: z.uuid().nullable(),
  publishedAt: z.string().nullable(),
  updatedAt: z.string(),
});
export type TranslationStringDto = z.infer<typeof translationStringDto>;

export const translationKeyDto = z.object({
  key: z.string(),
  namespace: z.string(),
  description: z.string().nullable(),
  maxLength: z.number().int().nonnegative(),
  /** All strings for this key, one per locale (empty array if none authored yet). */
  strings: z.array(translationStringDto),
});
export type TranslationKeyDto = z.infer<typeof translationKeyDto>;

export const listTranslationsQuery = z.object({
  namespace: z.string().optional(),
  locale: localeSchema.optional(),
  status: stringStatus.optional(),
  // 'missing' returns only keys lacking a published value for the given locale.
  filter: z.enum(['all', 'missing', 'draft']).optional().default('all'),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).optional().default(200),
  offset: z.coerce.number().int().nonnegative().optional().default(0),
});
export type ListTranslationsQuery = z.infer<typeof listTranslationsQuery>;

export const upsertTranslationStringInput = z.object({
  key: z.string().min(1).max(240),
  locale: localeSchema,
  value: z.string().min(0).max(10_000),
  /** Defaults to 'draft' on create; pass 'published' to publish in one step. */
  status: stringStatus.optional().default('draft'),
});
export type UpsertTranslationStringInput = z.infer<typeof upsertTranslationStringInput>;

export const publishTranslationStringsInput = z.object({
  ids: z.array(z.uuid()).min(1).max(500),
});
export type PublishTranslationStringsInput = z.infer<typeof publishTranslationStringsInput>;

export const translationNamespaceDto = z.object({
  namespace: z.string(),
  description: z.string().nullable(),
  audience: z.string().nullable(),
  /** Pre-counted for the editor sidebar. */
  totalKeys: z.number().int(),
});
export type TranslationNamespaceDto = z.infer<typeof translationNamespaceDto>;
